"use client";

import { ArrowRight, Layers, Palette, Sofa } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FundingTiers() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const cardsReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    number: "§ 01",
    title: "CREDIT ARCHITECTURE",
    Icon: Palette,
    body: "We don't just repair; we engineer. Our team restructures your personal profile to meet the stringent requirements of high-limit institutional lenders and private capital groups.",
    italic: "Precision in every data point.",
    offset: "md:translate-y-0"
  }, {
    number: "§ 02",
    title: "CAPITAL SOURCING",
    Icon: Layers,
    body: "Accessing elite funding requires more than an application. We leverage a global network of private equity and boutique lenders to secure non-dilutive capital for your ventures.",
    italic: "Liquidity is the ultimate leverage.",
    offset: "md:-translate-y-12"
  }, {
    number: "§ 03",
    title: "WEALTH STRATEGY",
    Icon: Sofa,
    body: "Funding is the fuel, but strategy is the engine. We provide high-end coaching to transform immediate capital into long-term generational wealth through strategic asset acquisition.",
    italic: "Building legacies, not just balances.",
    offset: "md:translate-y-12"
  }];
  return <section data-section-id="3504" id="funding-tiers" className="bg-background text-foreground py-24 md:py-36 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={header.ref} className={`text-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono font-bold tracking-[0.2em] uppercase mb-6" style={{
          color: "var(--primary)",
          fontSize: "12px"
        }}>
            OUR DISCIPLINE
          </p>
          <h2 className="font-default font-medium text-5xl md:text-7xl lg:text-8xl tracking-tighter text-foreground leading-[1.1] mb-6">
            THREE <span style={{
            color: "var(--primary)"
          }}>PILLARS</span>
          </h2>
          <p className="font-serif italic text-lg text-muted-foreground max-w-xl mx-auto">
            Where every financial decision is deliberate.
          </p>
        </div>

        {}
        <div ref={cardsReveal.ref} className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8">
          {pillars.map((pillar, i) => {
          const Icon = pillar.Icon;
          return <div key={i} data-index={i} className={`group transition-all duration-1000 ease-out ${pillar.offset} ${cardsReveal.isVisible ? "opacity-100" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className="relative h-full min-h-[520px] p-10 flex flex-col rounded-md border backdrop-blur-2xl transition-all duration-500 ease-out group-hover:-translate-y-2" style={{
              backgroundColor: "var(--secondary)",
              borderColor: "var(--border)",
              boxShadow: "var(--shadow-3xl)"
            }}>
                  {}
                  <div className="absolute inset-0 rounded-md border opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                borderColor: "var(--primary)"
              }} />

                  {}
                  <div className="flex items-center justify-between mb-10">
                    <span className="font-mono font-bold tracking-widest uppercase" style={{
                  color: "var(--primary)",
                  fontSize: "12px"
                }}>
                      {pillar.number}
                    </span>
                  </div>

                  {}
                  <div className="mb-10">
                    <Icon className="w-16 h-16 transition-transform duration-500 group-hover:scale-110" strokeWidth={1} style={{
                  color: "var(--primary)"
                }} />
                  </div>

                  {}
                  <h3 className="font-default font-semibold text-2xl md:text-3xl text-foreground mb-5 tracking-wider uppercase leading-tight">
                    {pillar.title}
                  </h3>

                  {}
                  <div className="h-[1px] w-12 mb-6" style={{
                backgroundColor: "var(--primary)"
              }} />

                  {}
                  <p className="font-default font-normal text-[16px] leading-relaxed text-foreground/90 mb-2">
                    {pillar.body}
                  </p>
                  <p className="font-serif italic text-[16px] leading-relaxed text-muted-foreground mb-10">
                    {pillar.italic}
                  </p>

                  {}
                  <div className="mt-auto pt-6">
                    <Button asChild variant="ghost" className="h-auto p-0 font-mono font-bold tracking-widest uppercase text-xs hover:bg-transparent group/btn" style={{
                  color: "var(--primary)"
                }}>
                      <Link to="/ServicesPage">
                        EXPLORE SOLUTIONS
                        <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}